import { EntityKind, TileType, type EntityState } from '@game/protocol';

import { collapsiblePanel, type PanelHeader } from './panel.ts';

/**
 * Minimapa (PRD §6).
 *
 * ## Um pixel por tile, e não uma câmera reduzida
 *
 * A alternativa óbvia seria uma segunda câmera do Phaser com zoom baixo. Ela custaria uma
 * passada de render inteira por quadro para desenhar o mesmo cenário duas vezes, e daria um
 * resultado pior: sprites de 32 px reduzidos a 3 viram borrões. O minimapa não é uma vista
 * pequena do jogo, é um **mapa** — cor sólida por tipo de terreno, ponto por criatura —, e
 * um pixel por tile é a forma direta de desenhá-lo.
 *
 * ## Só o que já chegou
 *
 * O terreno vem por chunk (§3.5), então o cliente conhece um recorte do mundo, não o mundo.
 * O que ainda não chegou fica preto, e isso não é uma limitação disfarçada de recurso: é
 * honesto. Um minimapa que mostrasse o mapa inteiro estaria entregando de graça a
 * informação que o streaming de terreno existe para não entregar — onde ficam as paredes
 * de uma caverna que ninguém explorou.
 *
 * ## Redesenhado sob demanda, não por quadro
 *
 * O terreno é estático e o jogador anda a cada 150ms. Redesenhar a 60fps seria gastar
 * quadro para repetir o mesmo desenho dez vezes; o `draw` é chamado quando a posição de
 * grade muda, quando chega chunk novo, ou quando o andar muda.
 *
 * ## Clicar no mapa é clicar no mundo
 *
 * Um pixel por tile também é uma **grade de endereços**: a conversão de volta é a mesma
 * conta do desenho lida ao contrário, e por isso o clique no minimapa pode alimentar o
 * mesmo `WalkPath` do clique no cenário. É o que o mapa promete ao ser um mapa — quem olha
 * para a clareira do outro lado do bosque quer ir até lá, e sem isto teria de reencontrá-la
 * no cenário andando às cegas na direção certa.
 *
 * O alcance é maior que o da tela: 32 tiles em cada eixo contra os ~11 do campo de visão.
 * Quem paga essa conta é o teto de busca do `walk-path.ts`, dimensionado para esta janela.
 */

/** Lado da janela desenhada, em tiles. Ímpar para o jogador cair no pixel do centro. */
const VIEW = 65;

/** Lado do canvas em pixels. Cada tile vira um quadrado de `SCALE`. */
const SCALE = 3;

/**
 * Cor por tipo de tile.
 *
 * Deriva da paleta do jogo o bastante para o minimapa parecer o mesmo mundo — grama verde,
 * água azul, pedra cinza —, e **achata** o que não importa nesta escala: os quatro solos
 * caminháveis de superfície viram tons próximos, porque a pergunta que o minimapa responde
 * é "dá para passar por aqui", e não "de que material é o chão".
 */
const COLORS: Readonly<Record<number, string>> = {
  [TileType.Floor]: '#3c5a34',
  [TileType.Dirt]: '#5a4a34',
  [TileType.Sand]: '#7a6c48',
  [TileType.Pavement]: '#5c5f66',
  [TileType.CaveFloor]: '#33353b',
  [TileType.Wall]: '#14181e',
  [TileType.Water]: '#25476b',
  [TileType.Tree]: '#24401f',
  [TileType.Rock]: '#4a4d52',
  [TileType.Bush]: '#2e4a26',
  [TileType.Sanctuary]: '#4a6f8a',
  [TileType.StairsUp]: '#c9a227',
  [TileType.StairsDown]: '#c9a227',
  [TileType.Portal]: '#a05fd0',

  // O subsolo e os mecanismos (PRD §15.4, §15.6).
  //
  // A pergunta que o minimapa responde é "dá para passar por aqui", e a resposta para a
  // maioria destes é não — então eles são achatados na mesma família da rocha. As exceções
  // são as que **mudam a decisão de rota**: lava, que é um obstáculo que se reconhece de
  // longe; e o buraco, que é por onde se troca de andar sem querer. Um degrau que não
  // aparece no mapa é um degrau que o jogador só acha por acaso.
  [TileType.Bones]: '#3d3a33',
  [TileType.Lava]: '#c2450e',
  [TileType.Crystal]: '#3f8fa8',
  [TileType.Mushroom]: '#6d3a44',
  [TileType.Web]: '#7c828c',
  [TileType.Stalagmite]: '#4a4d52',
  [TileType.OreVein]: '#5c4a24',
  [TileType.DoorClosed]: '#8a5a2b',
  [TileType.DoorOpen]: '#c8a06a',
  [TileType.DoorLocked]: '#6b3f1c',
  [TileType.Hole]: '#0b0906',
  [TileType.Lever]: '#9a3b3b',
  // O barco do cais: cor de casco, e não de água. Ele é o desenho que diz "daqui se
  // navega", e apagá-lo no minimapa esconderia justamente o ponto que o jogador procura
  // quando quer sair da ilha.
  [TileType.Boat]: '#8a5f33',
  [TileType.Chest]: '#d2b04a',
  // A cidade (PRD §15.8). A calçada é o único dos seis que muda a decisão de rota, e por isso
  // é o único com cor própria: é por ela que se lê a malha de ruas no minimapa, e é a malha que
  // responde "por onde eu volto ao templo" numa cidade de dezessete portas.
  //
  // Balcão, cerca, caixaria, lampião e placa bloqueiam, e são achatados na família da
  // construção — a pergunta aqui é "dá para passar", e a resposta dos cinco é não. Desenhar
  // cada um com a cor da própria arte encheria a planta da cidade de confete e esconderia
  // justamente o que importa nesta escala.
  [TileType.Cobble]: '#6e6a62',
  [TileType.Counter]: '#4a3f34',
  [TileType.Fence]: '#4a3f34',
  [TileType.Crate]: '#4a3f34',
  [TileType.Lamp]: '#4a3f34',
  [TileType.Sign]: '#4a3f34',
};

/**
 * O tile sob um ponto do canvas — o desenho lido ao contrário.
 *
 * `width` e `height` são o tamanho **exibido**, que não é o do canvas assim que alguém
 * aperta o zoom do navegador: a conta normaliza para a fração antes de multiplicar pela
 * janela, e sem isso o clique erraria o tile por um fator de escala.
 *
 * `null` para um ponto fora do mapa. Fora do **mundo** não é problema desta função: um
 * endereço negativo é recusado adiante por quem sabe onde ficam as bordas, e é o mesmo
 * "não dá" que um clique numa parede recebe.
 *
 * Separada da classe para ser testável sem navegador, pelo mesmo motivo de `walk-path.ts`
 * viver fora do Phaser: um erro de meio tile aqui não quebra nada de forma visível — só faz
 * o personagem andar para o lugar errado, e ninguém desconfia da conta.
 */
export function tileFromPoint(
  offsetX: number,
  offsetY: number,
  width: number,
  height: number,
  centerX: number,
  centerY: number,
): { x: number; y: number } | null {
  if (width <= 0 || height <= 0) return null;

  const column = Math.floor((offsetX / width) * VIEW);
  const row = Math.floor((offsetY / height) * VIEW);
  if (column < 0 || row < 0 || column >= VIEW || row >= VIEW) return null;

  const half = (VIEW - 1) / 2;
  return { x: centerX - half + column, y: centerY - half + row };
}

/** O que o minimapa precisa saber sobre o terreno. Ver `render/terrain.ts`. */
export interface MinimapSource {
  readonly width: number;
  readonly height: number;
  at(x: number, y: number, z: number): number;
}

export interface MinimapCallbacks {
  /** Clique esquerdo num tile do mapa. Ver `GameScene.startWalk`. */
  onPick(x: number, y: number): void;
}

/**
 * Quanto tempo a marca de um clique fica no mapa.
 *
 * Mais longa que os 320ms do anel do cenário (`render/pointer-marks.ts`) porque aqui ela
 * não cresce nem some aos poucos — é um anel de seis pixels parado, e um piscar de olhos
 * mais curto que isto não chega a ser lido.
 */
const PING_MS = 700;

/** Verde de "estou indo"; vermelho de "não sei chegar aí". Os mesmos do cenário. */
const PING_OK = '#7fd4a0';
const PING_DENIED = '#ff6b6b';

export class Minimap {
  private readonly root: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D | null;
  private readonly label: HTMLElement;
  private readonly header: PanelHeader;

  private open = false;

  /**
   * O centro do último desenho — e a origem da conta que converte pixel em tile.
   *
   * Negativo é "nada desenhado ainda", e é o que faz o clique antes do primeiro `draw` não
   * virar um endereço inventado. Quem pede redesenho não mexe mais aqui: para isso existe
   * o `dirty`, porque um centro falsificado para forçar a pintura seria também um centro
   * falsificado para o clique.
   */
  private centerX = -1;
  private centerY = -1;
  private centerZ = -1;
  private dirty = true;

  /**
   * O último terreno recebido, guardado para o repintar que o clique precisa.
   *
   * O desenho normal é empurrado pela cena a cada mudança de grade; a marca de clique
   * aparece e some **entre** duas dessas, e sem o terreno em mãos apagá-la exigiria esperar
   * o jogador dar um passo.
   */
  private source: MinimapSource | null = null;
  private entities: readonly EntityState[] = [];
  private selfId = 0;

  private ping: { x: number; y: number; ok: boolean } | null = null;
  private pingTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(root: HTMLElement, callbacks: MinimapCallbacks) {
    this.root = root;
    this.root.className = 'minimap';

    this.canvas = document.createElement('canvas');
    this.canvas.width = VIEW * SCALE;
    this.canvas.height = VIEW * SCALE;
    this.canvas.className = 'minimap-canvas';

    this.label = document.createElement('div');
    this.label.className = 'minimap-label';

    // Recolhido, sobra a barra de título no canto — 20 px em vez de 195. É o estado de quem
    // quer o mapa a um clique de distância sem ele tapando o canto de onde vem a criatura,
    // que é a mesma razão de os painéis não flutuarem sobre o mundo (ver `style.css`).
    this.header = collapsiblePanel(this.root, 'minimapa', 'Minimapa', {
      // Mesmo motivo do `toggle` abaixo: enquanto recolhido o mapa não é redesenhado, e sem
      // invalidar aqui o jogador reencontraria o desenho de onde estava quando recolheu.
      onToggle: () => {
        this.dirty = true;
      },
    });

    // Só o botão esquerdo. O direito é o menu de contexto do jogo, e o do meio cola texto
    // em algumas plataformas — nenhum dos dois é um pedido de "ande até ali".
    this.canvas.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;

      const tile = this.tileAt(event);
      if (!tile) return;

      // Sem isto o arraste iniciado sobre o canvas vira seleção de texto do painel, e o
      // cursor de "não pode" acompanha o ponteiro pelo resto do gesto.
      event.preventDefault();
      callbacks.onPick(tile.x, tile.y);
    });

    this.root.append(this.header.root, this.canvas, this.label);
    this.context = this.canvas.getContext('2d');

    this.render();
  }

  get isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    this.open = !this.open;
    this.render();
    // Força o próximo `draw` a redesenhar: o mapa pode ter mudado enquanto estava fechado.
    this.dirty = true;
  }

  /** Some ao trocar de região: o mapa de lá não vale mais nada. */
  reset(): void {
    this.centerX = -1;
    this.centerY = -1;
    this.centerZ = -1;
    this.dirty = true;
    this.source = null;
    this.entities = [];
    this.clearPing();
    this.context?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * O tile sob um ponto do ponteiro, ou `null` se o mapa ainda não sabe onde está o jogador.
   *
   * Um centro negativo é "nada desenhado ainda" — entre entrar na região e o primeiro
   * `draw` o canvas está preto, e um clique nele viraria um endereço inventado.
   */
  private tileAt(event: PointerEvent): { x: number; y: number } | null {
    if (this.centerX < 0) return null;

    const rect = this.canvas.getBoundingClientRect();

    return tileFromPoint(
      event.clientX - rect.left,
      event.clientY - rect.top,
      rect.width,
      rect.height,
      this.centerX,
      this.centerY,
    );
  }

  /**
   * Marca o tile pedido por um clique: anel verde para "estou indo", vermelho para a recusa.
   *
   * A recusa é a metade que faz o gesto ser aprendível, pelo mesmo motivo do anel do cenário
   * (`render/pointer-marks.ts`) — e aqui ela pesa mais, porque o destino de um clique no
   * minimapa costuma estar **fora da tela**: o anel do mundo é desenhado num tile que
   * ninguém está vendo, e sem esta marca o clique que não acha caminho é indistinguível de
   * um clique que o jogo não registrou.
   */
  showPing(x: number, y: number, ok: boolean): void {
    if (!this.open || this.header.collapsed) return;

    if (this.pingTimer !== null) clearTimeout(this.pingTimer);
    this.ping = { x, y, ok };
    this.pingTimer = setTimeout(() => {
      this.ping = null;
      this.pingTimer = null;
      this.paint();
    }, PING_MS);

    this.paint();
  }

  private clearPing(): void {
    if (this.pingTimer !== null) clearTimeout(this.pingTimer);
    this.pingTimer = null;
    this.ping = null;
  }

  /**
   * Desenha a janela em torno do jogador, se ela mudou.
   *
   * `force` existe para o chunk novo: o terreno mudou sem ninguém ter andado, e sem ele o
   * pedaço recém-recebido só apareceria no próximo passo.
   */
  draw(
    source: MinimapSource,
    x: number,
    y: number,
    z: number,
    entities: readonly EntityState[],
    selfId: number,
    force = false,
  ): void {
    // Guardados antes de qualquer saída antecipada: o clique repinta sozinho, entre dois
    // passos do jogador, e precisa deste terreno mesmo nos quadros em que nada mudou.
    this.source = source;
    this.entities = entities;
    this.selfId = selfId;

    // Recolhido é tão fechado quanto fechado, do ponto de vista do desenho: o canvas está em
    // `display: none` e pintar 4.225 tiles nele é trabalho que ninguém vê. Quem devolve o
    // desenho ao expandir é o `onToggle` do cabeçalho.
    if (!this.open || this.header.collapsed || !this.context) return;
    if (!force && !this.dirty && x === this.centerX && y === this.centerY && z === this.centerZ) {
      return;
    }

    this.centerX = x;
    this.centerY = y;
    this.centerZ = z;
    this.dirty = false;

    this.paint();
  }

  /**
   * Pinta o que já está guardado.
   *
   * Separado do `draw` porque a marca de clique nasce e morre sem ninguém ter andado: ela
   * precisa repintar a partir do mesmo estado, e não de argumentos novos que a cena não tem
   * motivo para produzir naquele instante.
   */
  private paint(): void {
    const source = this.source;
    if (!this.open || this.header.collapsed || !this.context || !source) return;

    const x = this.centerX;
    const y = this.centerY;
    const z = this.centerZ;

    const half = (VIEW - 1) / 2;
    const context = this.context;

    // Preto no fundo é o "ainda não recebi": tudo que não for pintado fica assim.
    context.fillStyle = '#05070a';
    context.fillRect(0, 0, this.canvas.width, this.canvas.height);

    for (let row = 0; row < VIEW; row++) {
      for (let column = 0; column < VIEW; column++) {
        const tileX = x - half + column;
        const tileY = y - half + row;
        if (tileX < 0 || tileY < 0 || tileX >= source.width || tileY >= source.height) continue;

        const tile = source.at(tileX, tileY, z);
        const color = COLORS[tile];
        if (!color) continue;

        context.fillStyle = color;
        context.fillRect(column * SCALE, row * SCALE, SCALE, SCALE);
      }
    }

    // Criaturas por cima do terreno, e o próprio jogador por último — ele nunca pode ficar
    // escondido atrás de um monstro em cima dele.
    for (const entity of this.entities) {
      if (entity.id === this.selfId || entity.z !== z) continue;

      const column = entity.x - x + half;
      const row = entity.y - y + half;
      if (column < 0 || row < 0 || column >= VIEW || row >= VIEW) continue;

      context.fillStyle = entity.kind === EntityKind.Player ? '#7fd4a0' : '#d9534f';
      context.fillRect(column * SCALE, row * SCALE, SCALE, SCALE);
    }

    // O jogador é um losango branco de 5 px: forma diferente de tudo o mais, para ser
    // achado de relance num campo de quadradinhos.
    const center = half * SCALE + SCALE / 2;
    context.fillStyle = '#f4f7fa';
    context.beginPath();
    context.moveTo(center, center - 3);
    context.lineTo(center + 3, center);
    context.lineTo(center, center + 3);
    context.lineTo(center - 3, center);
    context.closePath();
    context.fill();

    // A marca do clique por último: ela responde a um gesto que acabou de acontecer, e um
    // rato desenhado por cima dela apagaria justamente a resposta.
    const ping = this.ping;
    if (ping) {
      const px = (ping.x - x + half) * SCALE + SCALE / 2;
      const py = (ping.y - y + half) * SCALE + SCALE / 2;

      context.strokeStyle = ping.ok ? PING_OK : PING_DENIED;
      context.lineWidth = 1.5;
      context.beginPath();
      context.arc(px, py, 4, 0, Math.PI * 2);
      context.stroke();
    }

    this.label.textContent = `${x}, ${y} · andar ${z}`;
  }

  private render(): void {
    this.root.classList.toggle('visible', this.open);
  }
}
